import { SearchInput } from "@bridgesplit/ui";
import { LendingVaultExpanded } from "@bridgesplit/abf-react";
import { ExternalYieldSource, StrategyDuration } from "@bridgesplit/abf-sdk";
import { DispatchType } from "@bridgesplit/react";
import { decimalPercentToUi } from "@bridgesplit/utils";

import { useManagerViewContext } from "./ManagerViewContext";
import {
    CollateralAllocationForm,
    CollateralForm,
    initialPrincipalCapsForm,
    initialSettingsForm,
    PrincipalCapsForm,
    RateForm,
    SettingForm
} from "./type";
import { convertVaultExpandedToMintToInfo, convertVaultExpandedToMintToRateInfo } from "./util";

export enum FormType {
    COLLATERAL_FORM = "collateralForm",
    RATE_FORM = "rateForm",
    COLLATERAL_ALLOCATION_FORM = "collateralAllocationForm",
    SETTINGS_FORM = "settingsForm",
    PRINCIPAL_CAPS_FORM = "principalCapsForm"
}

type FormTypes = {
    [FormType.COLLATERAL_FORM]: CollateralForm;
    [FormType.RATE_FORM]: RateForm;
    [FormType.COLLATERAL_ALLOCATION_FORM]: CollateralAllocationForm;
    [FormType.SETTINGS_FORM]: SettingForm;
    [FormType.PRINCIPAL_CAPS_FORM]: PrincipalCapsForm;
};

function useFormSearch<T extends FormType>(formType: T) {
    const {
        collateralForm,
        setCollateralForm,
        rateForm,
        setRateForm,
        collateralAllocationForm,
        setCollateralAllocationForm
    } = useManagerViewContext();

    type CurrentForm = FormTypes[T];

    const form = (() => {
        switch (formType) {
            case FormType.COLLATERAL_FORM:
                return collateralForm;
            case FormType.RATE_FORM:
                return rateForm;
            case FormType.COLLATERAL_ALLOCATION_FORM:
                return collateralAllocationForm;
        }
    })();

    const setForm = (() => {
        switch (formType) {
            case FormType.COLLATERAL_FORM:
                return setCollateralForm;
            case FormType.RATE_FORM:
                return setRateForm;
            case FormType.COLLATERAL_ALLOCATION_FORM:
                return setCollateralAllocationForm;
        }
    })();

    return {
        form,
        setForm: setForm as React.Dispatch<React.SetStateAction<CurrentForm>>
    };
}

export function SearchCollateral({ formType = FormType.COLLATERAL_FORM }: { formType?: FormType }) {
    const { form, setForm } = useFormSearch(formType);

    if (!form) return null;

    return (
        <SearchInput
            fullWidth
            placeholder="Search by token or address"
            onChange={(e) =>
                setForm((prev) => ({
                    ...prev,
                    search: e.target.value
                }))
            }
            value={form.search}
        />
    );
}

type FormResetHandler<T extends FormType> = (
    vaultExpanded: LendingVaultExpanded | undefined,
    strategyDurations?: StrategyDuration[] | undefined
) => (prev: FormTypes[T]) => Partial<FormTypes[T]>;

export const FORM_RESET_HANDLERS: Record<FormType, FormResetHandler<FormType>> = {
    [FormType.COLLATERAL_FORM]: (vaultExpanded) => (prev) => ({
        ...prev,
        ...updateCollateralForm(vaultExpanded)
    }),

    [FormType.RATE_FORM]: (vaultExpanded, strategyDurations) => (prev) => ({
        ...prev,
        ...updateRateForm(vaultExpanded, strategyDurations)
    }),

    [FormType.SETTINGS_FORM]: (vaultExpanded) => () => ({
        ...initialSettingsForm,
        idleCapital: vaultExpanded?.vaultStrategy.externalYieldInfo?.externalYieldSource ?? ExternalYieldSource.None,
        originationsEnabled: vaultExpanded?.vaultStrategy.strategy.originationsEnabled
    }),

    [FormType.PRINCIPAL_CAPS_FORM]: (vaultExpanded) => () => ({
        ...initialPrincipalCapsForm,
        ...updatePrincipalCaps(vaultExpanded)
    }),

    [FormType.COLLATERAL_ALLOCATION_FORM]: (vaultExpanded) => (prev) => ({
        ...prev,
        ...updateCollateralAllocationForm(vaultExpanded)
    })
};

function updateCollateralForm(vaultExpanded: LendingVaultExpanded | undefined): Partial<CollateralForm> {
    return {
        mintToInfo: convertVaultExpandedToMintToInfo(vaultExpanded)
    };
}

function updateRateForm(
    vaultExpanded: LendingVaultExpanded | undefined,
    strategyDurations: StrategyDuration[] | undefined
): Partial<RateForm> {
    const rateInfo = convertVaultExpandedToMintToRateInfo(vaultExpanded, strategyDurations);
    if (!rateInfo) return { mintToRateInfo: {}, disabledMints: {} };
    const disabledMints: Record<string, string[]> = {};

    for (const [mint, durations] of Object.entries(rateInfo)) {
        const disabledDurations: string[] = [];

        for (const [duration, info] of Object.entries(durations)) {
            if (!info.apyAmountUi) {
                disabledDurations.push(duration);
            }
        }

        if (disabledDurations.length > 0) {
            disabledMints[mint] = disabledDurations;
        }
    }

    return {
        mintToRateInfo: Object.fromEntries(Object.entries(rateInfo).map(([mint, info]) => [mint, info])),
        disabledMints
    };
}

// This function is used to update the collateral allocation form. It is used to get the max allocation pct from the vault strategy, and initialize the form that is used on the manager view.
function updateCollateralAllocationForm(
    vaultExpanded: LendingVaultExpanded | undefined
): Partial<CollateralAllocationForm> {
    if (!vaultExpanded?.vaultStrategy?.terms?.assetTerms) {
        return { mintToMaxAllocationPct: {} };
    }

    const mintToMaxAllocationPct: Record<string, string | null> = {};
    const assetTerms = vaultExpanded.vaultStrategy.terms.assetTerms;

    for (const [mint, assetTerm] of Object.entries(assetTerms)) {
        const maxAllocationPctDecimal = assetTerm.allocationInfo?.maxAllocationPct;
        mintToMaxAllocationPct[mint] = decimalPercentToUi(maxAllocationPctDecimal);
    }

    return { mintToMaxAllocationPct };
}

function updatePrincipalCaps(vaultExpanded: LendingVaultExpanded | undefined): Partial<PrincipalCapsForm> {
    return {
        max1hrBorrowCap: vaultExpanded?.vaultStrategy.terms.borrowCaps?.max1hr?.toString() ?? null,
        max24hrBorrowCap: vaultExpanded?.vaultStrategy.terms.borrowCaps?.max24hr?.toString() ?? null,
        maxOutstandingBorrowCap: vaultExpanded?.vaultStrategy.terms.borrowCaps?.maxOutstanding?.toString() ?? null,
        max1hrSupplyCap: vaultExpanded?.vaultStrategy.terms.supplyCaps?.max1hr?.toString() ?? null,
        max24hrSupplyCap: vaultExpanded?.vaultStrategy.terms.supplyCaps?.max24hr?.toString() ?? null,
        maxOutstandingSupplyCap: vaultExpanded?.vaultStrategy.terms.supplyCaps?.maxOutstanding?.toString() ?? null,
        max1hrWithdrawCap: vaultExpanded?.vaultStrategy.terms.withdrawCaps?.max1hr?.toString() ?? null,
        max24hrWithdrawCap: vaultExpanded?.vaultStrategy.terms.withdrawCaps?.max24hr?.toString() ?? null
    };
}
export function resetManagerOverviewForms(
    vaultExpanded: LendingVaultExpanded | undefined,
    strategyDurations: StrategyDuration[] | undefined,
    setCollateralForm: DispatchType<CollateralForm>,
    setRateForm: DispatchType<RateForm>,
    setSettingsForm: DispatchType<SettingForm>,
    setPrincipalCapsForm: DispatchType<PrincipalCapsForm>,
    setCollateralAllocationForm: DispatchType<CollateralAllocationForm>
) {
    setCollateralForm((prev) => ({
        ...prev,
        ...updateCollateralForm(vaultExpanded)
    }));

    setRateForm((prev) => ({
        ...prev,
        ...updateRateForm(vaultExpanded, strategyDurations)
    }));

    setCollateralAllocationForm((prev) => ({
        ...prev,
        ...updateCollateralAllocationForm(vaultExpanded)
    }));

    setSettingsForm((prev) => ({
        ...prev,
        ...initialSettingsForm
    }));

    setPrincipalCapsForm((prev) => ({
        ...prev,
        ...updatePrincipalCaps(vaultExpanded)
    }));
}
